import { DEFAULT_PROPERTIES } from "./properties.js";
import { CoreNode } from "../components/core-node.js";
import { uidDerivation } from "../utils/uid-derivation.js";
import { objects } from "../utils/objects.js";
import { types } from "../utils/types.js";
import { configs } from "./configs.js";
import { descriptors } from "./descriptors.js";
import { evaluateUid, evaluateLinkedExpression, validateProperty } from "./evaluation.js";
import { Event, events } from "./events.js";
import { createSingletonLogger } from "./singleton-logger.js";
import { nodes } from "./nodes.js";
import { getDefaultPropertyValue } from "./properties.js";
import { time } from "./time.js";
import { NodeProxy } from "../components/node-proxy.js";
import { trace } from "./trace.js";

const ARTIFICIAL_EVENT_NAME = "Artificially created Event in Core Engine";
class CoreEngine {
  static componentName = "core-engine";

  _components = [];
  _implementations = new Map();
  #logger;
  #runtimeImplementations = new Map();
  #isInitialized = false;

  constructor() {
    this.#logger = createSingletonLogger(this);
  }

  // TODO event.data should be resolved in config, event should not be forwarded
  // TODO the artificial event concept is confusing — time should come from config, not event
  create(originalConfig, message) {
    const event = this._getEvent(message);

    const clonedConfig = objects.clone(originalConfig);

    const uid = this._isUidValid(clonedConfig.uid)
      ? clonedConfig.uid
      : uidDerivation.deriveUid(clonedConfig, (candidate) => !!nodes.get(candidate));
    clonedConfig.uid = evaluateUid(uid, event);

    const targetNode = nodes.get(clonedConfig.uid);

    if (targetNode && typeof targetNode.coreImplementation.execute !== "function") {
      this.#logger.warn(
        `Node with uid [${clonedConfig.uid}] and namespace [${clonedConfig.namespace}] already exists. Creation declined.`
      );
      return;
    }

    const descriptor = descriptors.get(clonedConfig.namespace);
    if (!descriptor) {
      this.#logger.error(
        `Descriptor for uid [${clonedConfig.uid}] and namespace [${clonedConfig.namespace}] was not found. Creation declined.`
      );
      return null;
    }

    const implementation = this._resolveImplementation(clonedConfig.namespace, descriptor);

    if (!implementation) {
      this.#logger.error(
        `Core implementation for uid [${clonedConfig.uid}] and namespace [${clonedConfig.namespace}] was not found. Creation declined`
      );
      return null;
    }

    const config = configs.resolveConfig(clonedConfig, message);

    this._cleanupConfig(config);

    if (targetNode && typeof targetNode.coreImplementation.execute === "function") {
      targetNode.coreImplementation.execute(targetNode, config, event);
      return;
    }

    const cNode = new CoreNode({
      uid: clonedConfig.uid,
      templateUid: originalConfig.templateUid,
      namespace: originalConfig.namespace,
      coreImplementation: implementation,
      props: config,
    });

    nodes.set(clonedConfig.uid, cNode);

    cNode.coreImplementation.initNode(cNode, event);
    cNode._initialized = true;

    trace.record(originalConfig.namespace, clonedConfig.uid, "create", config);

    // running execute method to support component lifecycle
    if (typeof cNode.coreImplementation.execute === "function") {
      cNode.coreImplementation.execute(cNode, config, event);
    }

    return cNode;
  }

  update(message, originalEvent) {
    const clonedData = objects.clone(message);

    const event = this._getEvent(originalEvent);

    clonedData.uid = evaluateUid(message.uid, event);

    const cNode = nodes.get(clonedData.uid);

    if (!cNode) {
      this.#logger.warn(`cNode uid [${clonedData.uid}] does not exist`);
      return;
    }

    const descriptor = descriptors.get(cNode.namespace);

    if (descriptor.methods[clonedData.key]) {
      trace.record(cNode.namespace, clonedData.uid, clonedData.key, clonedData.value);
      this._executeMethod(cNode, clonedData, event);
      return;
    }

    if (descriptor.properties[clonedData.key]) {
      if (cNode.props[clonedData.key] === clonedData.value) return;
      if (typeof clonedData.value !== 'number') {
        trace.record(cNode.namespace, clonedData.uid, clonedData.key, clonedData.value);
      }
      this._updateProperty(clonedData);
      return;
    }

    if (this._isDynamicGroupProperty(clonedData.key, descriptor)) {
      if (cNode.props[clonedData.key] === clonedData.value) return;
      if (typeof clonedData.value !== 'number') {
        trace.record(cNode.namespace, clonedData.uid, clonedData.key, clonedData.value);
      }
      this._updateProperty(clonedData);
      return;
    }

    trace.record(cNode.namespace, clonedData.uid, clonedData.key, clonedData.value);
    this._executeMethod(cNode, clonedData, event);
  }

  destroy(uid, message) {
    this.#logger.log(`Destroying node with uid [${uid}]`);

    // TODO uids should not contain snapshot tokens — this should be disallowed
    const evaluatedUid = evaluateUid(uid);
    const cNode = nodes.get(evaluatedUid);

    if (!cNode) {
      this.#logger.warn(`Node with uid ${evaluatedUid} does not exist. Destroy ignored.`);
      return;
    }

    trace.record(cNode.namespace, evaluatedUid, "destroy", null);
    cNode.coreImplementation.destroy(cNode);
    nodes.delete(evaluatedUid);
  }

  addImplementation(implementation) {
    if (this.#isInitialized) return;
    this._components.push(implementation);
  }

  registerImplementations() {
    for (const component of this._components) {
      if (!types.isString(component.namespace)) {
        this.#logger.critical("Malformed component's namespace. Should be a string");
        continue;
      }
      
      this._implementations.set(component.namespace, component);
    }
    
    this.#isInitialized = true;
    this.#logger.log("Component registration phase complete - runtime registration now enabled");
  }

  getProxyFor(uid, senderNode) {
    return new NodeProxy(uid, senderNode);
  }

  _isUidValid(uid) {
    if (!uid) {
      return false;
    }

    if (!/[\dA-z]/.test(uid)) {
      this.#logger.warn(`Invalid uid - [${uid}]. A random uid will be assigned`);
      return false;
    }

    return true;
  }

  _cleanupConfig(config) {
    for (const key of Object.keys(config)) {
      const isRedundant = DEFAULT_PROPERTIES.includes(key);
      if (isRedundant) {
        delete config[key];
      }
    }
    delete config.configSnippets;
  }

  _updateProperty(message) {
    const { uid, key: propertyName, value, sender } = message;

    const node = nodes.get(uid);

    if (!node) {
      this.#logger.warn(`Node with uid [${uid}] does not exist. Property update ignored`);
      return;
    }

    const descriptor = descriptors.get(node.namespace);
    const propertyDescriptor = descriptor.properties[propertyName];

    if (propertyDescriptor?.group) {
      const groupDescriptor = descriptor.properties[propertyDescriptor.group];
      if (groupDescriptor?.optional && !(propertyName in node.props)) {
        this._activateOptionalGroup(node, descriptor, propertyDescriptor.group, propertyName);
      }
    }

    const context = { uid, namespace: node.namespace };
    let evaluatedValue = evaluateLinkedExpression(value, propertyDescriptor, context).value;

    // Resolve namespaced configs (e.g. tween, tween:group) through the config pipeline
    if (evaluatedValue && typeof evaluatedValue === 'object' && evaluatedValue.namespace) {
      evaluatedValue = configs.resolveConfig({ ...evaluatedValue });
    }

    if (!propertyDescriptor) {
      if (this._isDynamicGroupProperty(propertyName, descriptor)) {
        if (node.props[propertyName] === evaluatedValue) return;
        node.coreImplementation.update(node, { key: propertyName, value: evaluatedValue, sender });
      }
      return;
    }

    if (node.props[propertyName] === evaluatedValue) return;

    if (typeof evaluatedValue === 'string' && propertyDescriptor.type !== 'string' && propertyDescriptor.type !== 'expression') {
      node.coreImplementation.update(node, { key: propertyName, value: evaluatedValue, sender });
    } else if (evaluatedValue && typeof evaluatedValue === 'object' && evaluatedValue.namespace) {
      node.coreImplementation.update(node, { key: propertyName, value: evaluatedValue, sender });
    } else {
      const { isValid } = validateProperty(evaluatedValue, propertyDescriptor, false, context);
      const resolvedValue = isValid ? evaluatedValue : getDefaultPropertyValue(propertyDescriptor);
      node.coreImplementation.update(node, { key: propertyName, value: resolvedValue, sender });
    }
  }

  _executeMethod(node, nodeData, message) {
    const { key: methodName, value } = nodeData;
    const namespace = `${node.namespace}:${methodName}`;
    const valueWithNamespace = { ...value, namespace };

    const methodConfig = configs.resolveConfig(valueWithNamespace, message);
    node.coreImplementation.update(node, { key: methodName, value: methodConfig }, message);
  }

  _isDynamicGroupProperty(key, descriptor) {
    const dotIndex = key.indexOf(".");
    if (dotIndex === -1) return false;
    const groupName = key.substring(0, dotIndex);
    const groupDescriptor = descriptor.properties[groupName];
    return groupDescriptor?.type === "group" && groupDescriptor?.dynamic;
  }

  _activateOptionalGroup(node, descriptor, groupName, excludeProperty) {
    for (const propDesc of Object.values(descriptor.properties)) {
      if (propDesc.group !== groupName) continue;
      if (propDesc.optional) continue;
      if (propDesc.name === excludeProperty) continue;
      if (propDesc.name in node.props) continue;

      node.coreImplementation.update(node, {
        key: propDesc.name,
        value: getDefaultPropertyValue(propDesc),
      });
    }
  }

  _resolveImplementation(namespace, descriptor) {
    let implementation = this._implementations.get(namespace);
    if (implementation) return implementation;

    let parentNamespace = descriptor.extends;
    while (parentNamespace) {
      implementation = this._implementations.get(parentNamespace);
      if (implementation) {
        this._implementations.set(namespace, implementation);
        return implementation;
      }
      const parentDescriptor = descriptors.get(parentNamespace);
      parentNamespace = parentDescriptor?.extends;
    }

    return null;
  }

  _getEvent(event) {
    return event ?? events.currentEvent ?? new Event(ARTIFICIAL_EVENT_NAME, time.current);
  }

  registerRuntimeComponent(implementation) {
    const namespace = implementation.namespace;
    
    if (!namespace) {
      this.#logger.error("Runtime component must have a namespace");
      return false;
    }
    
    if (this._implementations.has(namespace)) {
      this.#logger.error(`Component namespace [${namespace}] already exists. Registration ignored.`);
      return false;
    }
    
    this.#runtimeImplementations.set(namespace, implementation);
    this._implementations.set(namespace, implementation);
    
    this.#logger.log(`Runtime component ${namespace} registered successfully`);
    return true;
  }

  unregisterRuntimeComponent(namespace) {
    if (!this.#runtimeImplementations.has(namespace)) {
      if (this._implementations.has(namespace)) {
        this.#logger.error(`Component ${namespace} is a static component and cannot be unregistered at runtime`);
      } else {
        this.#logger.error(`Runtime component ${namespace} not found`);
      }
      return false;
    }
    
    this.#runtimeImplementations.delete(namespace);
    this._implementations.delete(namespace);
    
    this.#logger.log(`Runtime component ${namespace} unregistered successfully`);
    return true;
  }

  isRuntimeComponent(namespace) {
    return this.#runtimeImplementations.has(namespace);
  }

  getRuntimeComponentNamespaces() {
    return Array.from(this.#runtimeImplementations.keys());
  }
}

const coreEngine = new CoreEngine();
export { CoreEngine, coreEngine };
